import { queryOptions, useQuery } from '@tanstack/react-query';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { segmentOutIntegrationKeys, parseSegmentOutIntegration, type SegmentOutIntegration } from './entity';

/**
 * Segment source connector reads.
 *
 * `staleTime: 0` — the record is mutated by its own config page and by the page's
 * two route views, so a revisit must never serve a stale copy. Ember used
 * `store.queryRecord` for the same reason.
 *
 * The integrations directory and the connections overview do **not** read this
 * slot: they read the same underlying row through the legacy `segdotcom` shape,
 * under `statusKey(workspaceId, 'segdotcom')` in `services/db/integration`.
 */
const STALE_TIME = 0;

/**
 * @public The connector's config, or `null` when it has never been connected.
 *
 * **A 404 is the "not connected" answer, not a failure** — the backend has no
 * record until the connector is first connected, which is what puts the page in
 * its connect state. Every other status is a real error and propagates: the page
 * must be able to tell "not connected" from "we could not load it", or a 5xx
 * would render the connect form and a submit would collide with a record that
 * already exists.
 */
export async function getSegmentOutIntegration(workspaceId: number): Promise<SegmentOutIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getSegmentOut({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data.segment_out == null ? null : parseSegmentOutIntegration(data.segment_out);
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** @public */
export const getSegmentOutIntegrationQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: segmentOutIntegrationKeys.detail(workspaceId, 'segment-out'),
    queryFn: () => getSegmentOutIntegration(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * @public Non-suspense: the config page branches on `isPending` itself so the
 * form is never interactive against an empty record, matching how the Ember
 * template gated on `fetchIntegrationTask.isRunning`. The `journeys` handoff
 * reads it too, to decide whether the record belongs to the embedded app.
 */
export const useSegmentOutIntegration = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentOutIntegrationQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentOutIntegrationQueryOptions(workspaceId), ...queryConfig });
};
